/** A room's Files: everything shared in the room and its circles, by week, with a preview. */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import type { HostFilesResponse, TalkFile } from "../../shared/talk";
import { openAssets } from "../assets/store";
import { attachmentOf } from "../assets/library";
import { Button } from "../components/ui/Button";
import { Face } from "../components/ui/Face";
import { Icon } from "../components/ui/Icon";
import { Menu } from "../components/ui/Menu";
import { Sheet } from "../components/ui/Sheet";
import { useToast } from "../components/ui/Toast";
import { api } from "../lib/api";
import { DocPage, FileTile, Head, Poster } from "./parts";
import { Nav } from "./phone";
import { everyonePath, hitPath } from "./places";
import { useTalkContext } from "./TalkLayout";
import { useRemote } from "./useTalk";
import { dateWords, firstName, shortDay, weekOf } from "./when";

type Kind = "all" | "you" | "them";

const mine = (f: TalkFile) => f.by.kind === "host";

/** The line a file came with: its key is "line:<id>:<n>" or "post:<id>:<n>". */
const lineOf = (f: TalkFile) => f.key.split(":")[1] ?? "";

function Preview({ f, onShow, onPin }: { f: TalkFile; onShow: () => void; onPin: () => void }) {
  return (
    <>
      {f.kind === "picture" ? <img className="doc-pic" src={f.url} alt="" /> : f.kind === "video" && f.posterUrl ? <img className="doc-pic" src={f.posterUrl} alt="" /> : <DocPage name={f.name} />}
      <div className="fprev">
        <b>{f.name}</b>
        <small>
          {mine(f) ? "You" : firstName(f.by.name)} shared it in {f.where} · {dateWords(f.sharedAt)}
          {f.size ? ` · ${f.size}` : ""}
        </small>
        {f.opened ? (
          <span className="fprev-n">
            <Icon name="eye" /> Opened by {f.opened.toLocaleString("en-US")} {f.opened === 1 ? "person" : "people"}
          </span>
        ) : null}
      </div>
      <div className="fprev-acts">
        <Button size="s" icon="pin" onClick={onPin}>
          Pin it in Everyone
        </Button>
        <Button size="s" icon="chat" onClick={onShow}>
          Show in the chat
        </Button>
        <a className="btn s" href={f.url} target="_blank" rel="noreferrer noopener" download={f.kind === "link" ? undefined : f.name}>
          <Icon name="download" /> Download
        </a>
      </div>
    </>
  );
}

export function FilesScreen() {
  const { wid = "" } = useParams();
  const ctx = useTalkContext();
  const phone = ctx.lay === "phone";
  const upright = ctx.lay === "upright";
  const navigate = useNavigate();
  const toast = useToast();
  const { data, phase, reload } = useRemote<HostFilesResponse>(`talk:files:${wid}`, () => api.talk.files(wid), `/talk/rooms/${encodeURIComponent(wid)}/ws`);
  const [kind, setKind] = useState<Kind>("all");
  const [q, setQ] = useState("");
  const [pick, setPick] = useState<string | null>(null);
  const [sheet, setSheet] = useState(false);
  useEffect(() => {
    if (data) ctx.reloadRail();
  }, [data, ctx.reloadRail]);

  const files = data?.files ?? [];
  const shown = useMemo(
    () => files.filter((f) => (kind === "you" ? mine(f) : kind === "them" ? !mine(f) : true)).filter((f) => !q.trim() || f.name.toLowerCase().includes(q.trim().toLowerCase())),
    [files, kind, q],
  );
  const weeks = useMemo(() => {
    const out: { week: string; list: TalkFile[] }[] = [];
    for (const f of shown.toSorted((a, b) => b.sharedAt - a.sharedAt)) {
      const week = weekOf(f.sharedAt);
      const last = out.at(-1);
      if (last?.week === week) last.list.push(f);
      else out.push({ week, list: [f] });
    }
    return out;
  }, [shown]);
  const current = shown.find((f) => f.key === pick) ?? (phone || upright ? null : (shown[0] ?? null));

  if (!data) return phase === "error" ? <main className="main"><p className="empty-note">We couldn't open the files. Pick them again.</p></main> : <main className="main" />;
  const room = data.room;
  const counts = { all: files.length, you: files.filter(mine).length, them: files.filter((f) => !mine(f)).length };
  const chips: [Kind, string][] = [
    ["all", `All ${counts.all}`],
    ["you", `From you ${counts.you}`],
    ["them", `From members ${counts.them}`],
  ];

  const add = () =>
    openAssets({
      fits: "send",
      title: "Add a file to the room",
      cta: "Add it",
      onPick: (asset) => {
        const att = attachmentOf(asset);
        if (!att) {
          toast({ text: "That one is still getting ready. Try it again in a moment.", icon: "info" });
          return;
        }
        api.talk
          .sendEveryone(wid, { body: "", attachments: [att], clientId: crypto.randomUUID() })
          .then(() => (toast({ text: "Added to Everyone", icon: "check" }), reload()))
          .catch(() => toast({ text: "We couldn't add that. Try again.", icon: "info" }));
      },
    });
  const pin = (f: TalkFile) =>
    api.talk
      .pinFile(wid, f.key)
      .then(() => toast({ text: "Pinned in Everyone", icon: "pin" }))
      .catch(() => toast({ text: "We couldn't pin that. Try again.", icon: "info" }));
  const show = (f: TalkFile) => navigate(f.place && f.place.kind !== "direct" ? hitPath(f.place, lineOf(f)) : everyonePath(wid));
  const preview = current ? <Preview f={current} onShow={() => show(current)} onPin={() => void pin(current)} /> : null;

  if (phone) {
    return (
      <div className="hp">
        <Nav
          back="Rooms"
          title="Files"
          sub={room.title}
          right={
            <button type="button" className="hp-ib" aria-label="Add a file" onClick={add}>
              <Icon name="upload" />
            </button>
          }
        />
        <div className="hp-scroll">
          <div className="hp-page">
            <label className="hp-q in">
              <Icon name="search" />
              <input placeholder="Search files" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <div className="hp-chips">
              {chips.map(([k, label]) => (
                <button key={k} type="button" className={kind === k ? "fchip on" : "fchip"} onClick={() => setKind(k)}>
                  {label}
                </button>
              ))}
            </div>
            {weeks.map(({ week, list }) => (
              <div key={week}>
                <p className="hp-sub big">{week}</p>
                <section className="hp-card">
                  {list.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      className="hp-file"
                      onClick={() => {
                        setPick(f.key);
                        setSheet(true);
                      }}
                    >
                      <FileTile f={f} />
                      <span className="hp-file-t">
                        <b>{f.name}</b>
                        <small>
                          {mine(f) ? "You" : firstName(f.by.name)} · {f.where} · {dateWords(f.sharedAt)}
                        </small>
                      </span>
                      {f.opened ? (
                        <span className="hp-file-n">
                          <Icon name="eye" />
                          {f.opened}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </section>
              </div>
            ))}
            {files.length === 0 ? <p className="empty-note">Nothing has been shared in this room yet.</p> : null}
          </div>
        </div>
        <Sheet open={sheet && !!current} onClose={() => setSheet(false)} title={current?.name ?? ""}>
          <div className="sheet-b side-sheet">{preview}</div>
        </Sheet>
      </div>
    );
  }

  return (
    <>
      <main className="main">
        <Head
          pic={<Poster url={room.posterUrl} className="mh-pic" />}
          title="Files"
          sub="Everything shared in this room and its circles"
          right={
            <Button icon="upload" onClick={add}>
              Add a file
            </Button>
          }
        />
        <div className="fbar">
          <label className="fsearch">
            <Icon name="search" />
            <input placeholder="Search files" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <span className="fchips">
            {chips.map(([k, label]) => (
              <button key={k} type="button" className={kind === k ? "fchip on" : "fchip"} onClick={() => setKind(k)}>
                {label}
              </button>
            ))}
          </span>
        </div>
        <div className="flist">
          {weeks.map(({ week, list }) => (
            <div key={week} className="fgroup">
              <p className="fweek">{week}</p>
              {list.map((f) => (
                <div
                  key={f.key}
                  className={current?.key === f.key ? "frow on" : "frow"}
                  onClick={() => {
                    setPick(f.key);
                    if (upright) setSheet(true);
                  }}
                >
                  <FileTile f={f} />
                  <span className="frow-t">
                    <b>{f.name}</b>
                    <small>
                      {f.where} · {shortDay(f.sharedAt)}
                      {f.size ? ` · ${f.size}` : ""}
                    </small>
                  </span>
                  <span className="frow-by">
                    <Face name={f.by.name} photo={f.by.photoUrl} size={24} />
                    {mine(f) ? "You" : firstName(f.by.name)}
                  </span>
                  <span className="frow-n">{f.opened ? `${f.opened} opened` : ""}</span>
                  <span onClick={(e) => e.stopPropagation()}>
                    <Menu
                      label={`More for ${f.name}`}
                      buttonClassName="frow-more"
                      items={[
                        { label: "Open it", icon: "link", onSelect: () => void window.open(f.url, "_blank", "noopener") },
                        { label: "Show in the chat", icon: "chat", onSelect: () => show(f) },
                        { label: "Pin it in Everyone", icon: "pin", onSelect: () => void pin(f) },
                      ]}
                    />
                  </span>
                </div>
              ))}
            </div>
          ))}
          {files.length === 0 ? <p className="empty-note">Nothing has been shared in this room yet.</p> : null}
        </div>
      </main>
      <aside className="side">{preview}</aside>
      <Sheet open={upright && sheet && !!current} onClose={() => setSheet(false)} title={current?.name ?? ""}>
        <div className="sheet-b side-sheet">{preview}</div>
      </Sheet>
    </>
  );
}
